import { Reveal } from '../components/effects/Reveal';
import { Spotlight } from '../components/effects/Spotlight';

const VALUES = [
  {
    num: '01',
    tag: 'Data',
    title: 'Modernizamos tu base de datos',
    desc: 'Migramos sistemas legados a arquitecturas modernas en GCP, AWS o Azure, sin frenar tu operación diaria.',
    accent: 'from-etribe-blue/8 to-transparent',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="8" ry="2.5" />
        <path d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5" />
        <path d="M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
      </svg>
    ),
  },
  {
    num: '02',
    tag: 'IA',
    title: 'Aceleramos decisiones con IA',
    desc: 'Convertimos datos crudos en insights accionables con analítica avanzada, Machine Learning e IA generativa.',
    accent: 'from-etribe-purple/8 to-transparent',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
        <rect x="6" y="6" width="12" height="12" rx="2" />
        <rect x="10" y="10" width="4" height="4" />
      </svg>
    ),
  },
  {
    num: '03',
    tag: 'Tiempo real',
    title: 'Operamos en tiempo real',
    desc: 'Diseñamos pipelines y dashboards que entregan información al minuto, incluso en picos de demanda.',
    accent: 'from-etribe-yellow/12 to-transparent',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
  },
];

export default function ValueProps() {
  return (
    <section id="nosotros" className="py-[120px] relative overflow-hidden">
      {/* Background decoration */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none -z-10 opacity-60"
        style={{
          background:
            'radial-gradient(60% 40% at 0% 0%, rgba(16,3,163,.05), transparent 60%), radial-gradient(50% 40% at 100% 100%, rgba(255,200,54,.06), transparent 60%)',
        }}
      />

      <div className="container-etribe">
        <Reveal>
          <div className="max-w-[720px] mb-16">
            <p className="eyebrow"><span>Qué hacemos</span></p>
            <h2 className="text-[clamp(32px,3.6vw,48px)] mb-4">Qué hacemos por ti</h2>
            <p className="text-[clamp(17px,1.3vw,20px)] text-etribe-ink-2 leading-[1.55]">
              Tres frentes de trabajo, un solo objetivo: que tu organización decida con datos vivos y opere a la velocidad del negocio.
            </p>
          </div>
        </Reveal>

        <div className="grid gap-7 md:grid-cols-3">
          {VALUES.map((v, idx) => (
            <Reveal key={v.num} delay={idx * 120}>
              <Spotlight
                className="relative h-full rounded-[18px] bg-white border border-etribe-line overflow-hidden group hover:-translate-y-2 hover:shadow-card-lg hover:border-etribe-blue/20 transition-all duration-400"
                color="rgba(16,3,163,0.10)"
                size={520}
              >
                <article className="relative p-10 h-full flex flex-col" data-cursor="grow">
                  {/* Número gigante de fondo */}
                  <span
                    aria-hidden
                    className="absolute -top-4 -right-2 font-heading font-black text-[160px] leading-none text-etribe-blue/[0.04] group-hover:text-etribe-blue/[0.07] transition-colors duration-500 select-none pointer-events-none"
                  >
                    {v.num}
                  </span>

                  {/* Gradient accent en hover */}
                  <span
                    aria-hidden
                    className={`absolute inset-0 bg-gradient-to-br ${v.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}
                  />

                  {/* Eyebrow numerada */}
                  <div className="relative z-10 inline-flex items-center gap-2 mb-8">
                    <span className="font-heading text-[11px] tracking-[0.18em] text-etribe-blue font-bold">
                      {v.num}
                    </span>
                    <span className="w-6 h-px bg-etribe-yellow" />
                    <span className="font-heading text-[11px] tracking-[0.18em] uppercase text-etribe-gray-500">
                      {v.tag}
                    </span>
                  </div>

                  {/* Icon con halo */}
                  <div className="relative z-10 mb-8">
                    <div className="absolute inset-0 bg-etribe-yellow/15 rounded-[14px] blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative w-14 h-14 grid place-items-center text-etribe-blue bg-etribe-blue/[0.06] group-hover:bg-etribe-blue group-hover:text-etribe-yellow rounded-[14px] transition-all duration-400">
                      {v.icon}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="relative z-10 text-[clamp(22px,1.8vw,26px)] mb-4 leading-tight">
                    {v.title}
                  </h3>

                  {/* Divider amarillo animado */}
                  <span
                    aria-hidden
                    className="relative z-10 block h-0.5 w-10 bg-etribe-yellow mb-5 group-hover:w-20 transition-all duration-500"
                  />

                  {/* Description */}
                  <p className="relative z-10 text-etribe-ink-2 text-[15px] leading-[1.6] flex-1 text-justify hyphens-auto">
                    {v.desc}
                  </p>
                </article>
              </Spotlight>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
